import React, { useState, useEffect } from 'react';
import { generateGameId } from '../utils/gameUtils';
import BigBtn from '../design/components/BigBtn';
import ThemePicker from '../design/components/ThemePicker';
import { useIsDesktop } from '../hooks/useIsDesktop';

const PLAYER_NAME_STORAGE_KEY = '4options_player_name';

const readStoredName = () => {
  try {
    return localStorage.getItem(PLAYER_NAME_STORAGE_KEY) || '';
  } catch {
    return '';
  }
};

const MODES = [
  { id: 'trump-card', label: 'TRUMP CARD', glyph: '🃏', blurb: 'Pick the better stat. 4 players.' },
  { id: 'arrange',    label: 'ARRANGE',    glyph: '📊', blurb: 'Sort batters best→worst. Any count.' },
  { id: 'guess-spot', label: 'GUESS SPOT', glyph: '🎯', blurb: 'Drop a pin on the chart. Any count.' },
];

const inputBase = {
  width: '100%',
  fontFamily: 'var(--font-mono)',
  fontSize: 15,
  letterSpacing: 0.6,
  padding: '11px 14px',
  background: 'var(--c-cream)',
  color: 'var(--c-ink)',
  border: 'var(--border-ink)',
  borderRadius: 'var(--radius-btn)',
  boxShadow: 'var(--shadow-hard-sm)',
  outline: 'none',
  textTransform: 'uppercase',
  boxSizing: 'border-box',
};

const JoinScreen = ({ onJoinGame, error, connected }) => {
  const [playerName, setPlayerName] = useState(readStoredName);
  const [gameId, setGameId] = useState('');
  const [gameMode, setGameMode] = useState('trump-card');
  const isDesktop = useIsDesktop();

  const isJoining = gameId.trim().length > 0;

  useEffect(() => {
    try {
      if (playerName) localStorage.setItem(PLAYER_NAME_STORAGE_KEY, playerName);
    } catch {
      // localStorage unavailable (private mode, etc.) — just skip persisting
    }
  }, [playerName]);

  const handleJoin = () => {
    const name = playerName.trim();
    if (!name || !connected) return;
    const id = gameId.trim() || generateGameId();
    onJoinGame(name, id, gameMode);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleJoin();
  };

  const selectedMode = MODES.find((m) => m.id === gameMode);

  if (isDesktop) {
    return (
      <div className="pen-screen" style={{ height: '100%', display: 'flex', flexDirection: 'row' }}>
        {/* Left hero panel ~40% */}
        <div style={{
          flex: '0 0 40%',
          background: 'var(--c-yellow)',
          borderRight: 'var(--border-ink)',
          padding: '40px 32px',
          display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', top: -40, right: -40,
            width: 180, height: 180, borderRadius: '50%',
            background: 'var(--c-pink)', opacity: 0.35,
          }} />
          <div style={{ position: 'relative' }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              padding: '6px 10px',
              background: 'var(--c-cream)',
              border: 'var(--border-ink-sm)', borderRadius: 8,
              boxShadow: 'var(--shadow-hard-sm)',
            }}>
              <div style={{ fontSize: 18 }}>★</div>
              <div style={{
                fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700,
                letterSpacing: 0.6, color: 'var(--c-ink)',
              }}>STAT ATTACK</div>
            </div>
            <div style={{
              marginTop: 24,
              fontFamily: 'var(--font-display)', fontSize: 36, lineHeight: 1,
              color: 'var(--c-ink)', letterSpacing: -0.5,
              wordBreak: 'break-word',
            }}>DROP<br/>THE PIN.<br/>WIN THE<br/>ROOM.</div>
          </div>
          <div style={{ position: 'relative' }}>
            <div style={{
              fontFamily: 'var(--font-mono)', fontSize: 11, opacity: 0.7,
              color: 'var(--c-ink)', letterSpacing: 0.4, lineHeight: 1.5,
            }}>A CRICKET STATS PARTY GAME · UP TO 8 PLAYERS · DESIGNED FOR LIVE SESSIONS</div>
          </div>
        </div>

        {/* Right form panel ~60% */}
        <div style={{
          flex: 1,
          padding: '32px 36px',
          display: 'flex', flexDirection: 'column', gap: 18,
          background: 'var(--c-cream)',
          overflow: 'auto',
        }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ThemePicker />
          </div>

          <div>
            <div style={{
              fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: 0.8,
              color: 'var(--c-ink)', opacity: 0.75, marginBottom: 10,
            }}>GAME MODE</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {MODES.map((m) => {
                const sel = gameMode === m.id;
                return (
                  <button
                    key={m.id}
                    onClick={() => setGameMode(m.id)}
                    style={{
                      flex: 1, minWidth: 0,
                      padding: '10px 6px',
                      background: sel ? 'var(--c-yellow)' : 'var(--c-cream)',
                      border: 'var(--border-ink)',
                      borderRadius: 10,
                      boxShadow: sel ? 'var(--shadow-hard)' : 'var(--shadow-hard-sm)',
                      transform: sel ? 'translate(-1px,-1px)' : 'none',
                      transition: 'all 100ms var(--ease-snap)',
                      cursor: 'pointer',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                    }}
                  >
                    <div style={{ fontSize: 22 }}>{m.glyph}</div>
                    <div style={{
                      fontFamily: 'var(--font-display)', fontSize: 11, letterSpacing: 0.4,
                      color: 'var(--c-ink)', lineHeight: 1, textAlign: 'center',
                      whiteSpace: 'nowrap',
                    }}>{m.label}</div>
                  </button>
                );
              })}
            </div>
            <div style={{
              marginTop: 10, padding: '8px 12px',
              background: 'var(--c-ink)', color: 'var(--c-yellow)',
              borderRadius: 8, display: 'inline-block',
              fontFamily: 'var(--font-mono)', fontSize: 12, letterSpacing: 0.4,
            }}>{selectedMode?.blurb}</div>
          </div>

          <div>
            <div style={{
              fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: 0.8,
              color: 'var(--c-ink)', opacity: 0.75, marginBottom: 10,
            }}>YOUR DETAILS</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <input
                type="text"
                placeholder="YOUR NAME"
                maxLength="20"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                onKeyPress={handleKeyPress}
                style={{ ...inputBase, fontSize: 18, padding: '14px 16px' }}
              />
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="GAME ID OR LEAVE BLANK"
                  maxLength="10"
                  value={gameId}
                  onChange={(e) => setGameId(e.target.value.toUpperCase())}
                  onKeyPress={handleKeyPress}
                  style={{
                    ...inputBase, fontSize: 16, padding: '14px 16px',
                    paddingRight: isJoining ? 84 : 16,
                    border: isJoining ? '2px solid var(--c-green)' : 'var(--border-ink)',
                  }}
                />
                <div style={{
                  position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                  fontFamily: 'var(--font-display)', fontSize: 11, letterSpacing: 0.5,
                  background: isJoining ? 'var(--c-green)' : 'var(--c-yellow)',
                  color: 'var(--c-ink)',
                  padding: '4px 8px', borderRadius: 6,
                  border: 'var(--border-ink-sm)',
                  pointerEvents: 'none',
                }}>{isJoining ? 'JOINING' : 'NEW'}</div>
              </div>
            </div>
            <div style={{
              marginTop: 8,
              fontFamily: 'var(--font-mono)', fontSize: 12, opacity: 0.65,
              color: 'var(--c-ink)', lineHeight: 1.4,
            }}>
              {isJoining
                ? `↑ Entering someone's game ID to join their room`
                : '↑ Leave blank to create a room — share the ID with friends'}
            </div>
          </div>

          {error && (
            <div style={{
              fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 700,
              background: 'var(--c-pink)', color: 'var(--c-cream)',
              border: 'var(--border-ink-sm)', borderRadius: 6,
              padding: '8px 12px',
            }}>⚠ {error}</div>
          )}

          <div style={{ marginTop: 'auto' }}>
            <BigBtn
              onClick={handleJoin}
              primary
              disabled={!connected || !playerName.trim()}
            >
              {!connected
                ? 'CONNECTING…'
                : isJoining
                  ? `JOIN ROOM ${gameId} →`
                  : '✶ CREATE & JOIN →'}
            </BigBtn>
            <div style={{
              marginTop: 8, textAlign: 'center',
              fontFamily: 'var(--font-mono)', fontSize: 11, opacity: 0.5,
              color: 'var(--c-ink)', letterSpacing: 0.5,
            }}>
              {connected ? '● LIVE' : '○ CONNECTING'} · UP TO 8 PLAYERS PER ROOM
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pen-screen" style={{ minHeight: '100dvh' }}>
      {/* ── Top bar ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px 8px', gap: 8, flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: 'var(--c-yellow)',
            border: 'var(--border-ink)',
            boxShadow: 'var(--shadow-hard-sm)',
            display: 'grid', placeItems: 'center',
            fontSize: 15, flexShrink: 0,
          }}>★</div>
          <div style={{
            fontFamily: 'var(--font-display)', fontSize: 18, letterSpacing: -0.5,
            color: 'var(--c-ink)', lineHeight: 1,
          }}>STAT <span style={{
            background: 'var(--c-pink)', color: 'var(--c-cream)',
            padding: '0 5px', borderRadius: 5,
            border: '2px solid var(--c-ink)',
          }}>ATTACK</span></div>
        </div>
        <ThemePicker />
      </div>

      {/* ── Mode tabs ── */}
      <div style={{ padding: '0 16px 0', flexShrink: 0 }}>
        <div style={{
          fontFamily: 'var(--font-display)', fontSize: 10, letterSpacing: 0.7,
          color: 'var(--c-ink)', opacity: 0.7, marginBottom: 6,
        }}>GAME MODE</div>
        <div style={{ display: 'flex', gap: 6 }}>
          {MODES.map((m) => {
            const sel = gameMode === m.id;
            return (
              <button
                key={m.id}
                onClick={() => setGameMode(m.id)}
                style={{
                  flex: 1,
                  padding: '8px 4px',
                  background: sel ? 'var(--c-yellow)' : 'var(--c-cream)',
                  border: 'var(--border-ink)',
                  borderRadius: 10,
                  boxShadow: sel ? 'var(--shadow-hard)' : 'var(--shadow-hard-sm)',
                  transform: sel ? 'translate(-1px,-1px)' : 'none',
                  transition: 'all 100ms var(--ease-snap)',
                  cursor: 'pointer',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                }}
              >
                <div style={{ fontSize: 18 }}>{m.glyph}</div>
                <div style={{
                  fontFamily: 'var(--font-display)', fontSize: 9, letterSpacing: 0.4,
                  color: 'var(--c-ink)', lineHeight: 1, textAlign: 'center',
                }}>{m.label}</div>
              </button>
            );
          })}
        </div>
        {/* selected mode blurb */}
        <div style={{
          marginTop: 6, padding: '5px 10px',
          background: 'var(--c-ink)', color: 'var(--c-yellow)',
          borderRadius: 7, display: 'inline-block',
          fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: 0.4,
        }}>{selectedMode?.blurb}</div>
      </div>

      {/* ── Inputs ── */}
      <div style={{ padding: '12px 16px 0', flexShrink: 0 }}>
        <div style={{
          fontFamily: 'var(--font-display)', fontSize: 10, letterSpacing: 0.7,
          color: 'var(--c-ink)', opacity: 0.7, marginBottom: 6,
        }}>YOUR DETAILS</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <input
            type="text"
            placeholder="YOUR NAME"
            maxLength="20"
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            onKeyPress={handleKeyPress}
            style={inputBase}
          />
          {/* Game ID row with clear create/join split */}
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="GAME ID — BLANK = CREATE NEW"
              maxLength="10"
              value={gameId}
              onChange={(e) => setGameId(e.target.value.toUpperCase())}
              onKeyPress={handleKeyPress}
              style={{
                ...inputBase,
                paddingRight: isJoining ? 72 : 14,
                background: isJoining ? 'var(--c-cream)' : 'var(--c-cream)',
                border: isJoining ? '2px solid var(--c-green)' : 'var(--border-ink)',
              }}
            />
            {isJoining && (
              <div style={{
                position: 'absolute', right: 10, top: '50%',
                transform: 'translateY(-50%)',
                fontFamily: 'var(--font-display)', fontSize: 9, letterSpacing: 0.5,
                background: 'var(--c-green)', color: 'var(--c-ink)',
                padding: '3px 7px', borderRadius: 5,
                border: 'var(--border-ink-sm)',
                pointerEvents: 'none',
              }}>JOINING</div>
            )}
            {!isJoining && (
              <div style={{
                position: 'absolute', right: 10, top: '50%',
                transform: 'translateY(-50%)',
                fontFamily: 'var(--font-display)', fontSize: 9, letterSpacing: 0.5,
                background: 'var(--c-yellow)', color: 'var(--c-ink)',
                padding: '3px 7px', borderRadius: 5,
                border: 'var(--border-ink-sm)',
                pointerEvents: 'none',
              }}>NEW</div>
            )}
          </div>
        </div>

        {/* how-to-join hint */}
        <div style={{
          marginTop: 6,
          fontFamily: 'var(--font-mono)', fontSize: 10, opacity: 0.65,
          color: 'var(--c-ink)', lineHeight: 1.4,
        }}>
          {isJoining
            ? `↑ Entering someone's game ID to join their room`
            : '↑ Leave blank to create a room — share the ID with friends'}
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div style={{ padding: '10px 16px 0', flexShrink: 0 }}>
          <div style={{
            fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700,
            background: 'var(--c-pink)', color: 'var(--c-cream)',
            border: 'var(--border-ink-sm)', borderRadius: 6,
            padding: '6px 10px',
          }}>⚠ {error}</div>
        </div>
      )}

      {/* ── CTA — pushed to bottom, leaving one intentional gap above ── */}
      <div style={{ padding: '16px 16px 16px', flexShrink: 0, marginTop: 'auto' }}>
        <BigBtn
          onClick={handleJoin}
          primary
          disabled={!connected || !playerName.trim()}
        >
          {!connected
            ? 'CONNECTING…'
            : isJoining
              ? `JOIN ROOM ${gameId} →`
              : '✶ CREATE & JOIN →'}
        </BigBtn>
        <div style={{
          marginTop: 6, textAlign: 'center',
          fontFamily: 'var(--font-mono)', fontSize: 9, opacity: 0.5,
          color: 'var(--c-ink)', letterSpacing: 0.5,
        }}>
          {connected ? '● LIVE' : '○ CONNECTING'} · UP TO 8 PLAYERS PER ROOM
        </div>
      </div>
    </div>
  );
};

export default React.memo(JoinScreen);
